import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * SuperAdminLoginPage - Login form for platform super administrators
 * Connects to backend /api/auth/super-admin/login endpoint
 */
const SuperAdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { loginSuperAdmin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await loginSuperAdmin(email, password);
      // Redirect to super admin dashboard on success
      navigate('/super-admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showBackButton={true}>
      <form className="w-full" onSubmit={handleSubmit}>
        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            {/* Icon Glow */}
            <div className="absolute inset-0 blur-3xl opacity-40 bg-slate-600" />
            {/* Icon Container */}
            <div className="relative bg-gradient-to-br from-slate-600 to-slate-700 rounded-[1.5rem] p-6 shadow-2xl">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Platform Admin Login
          </h2>
          <p className="text-gray-500 text-base">
            Manage the entire Brillabee platform
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-5 mb-6">
          <div className="form-group">
            <label htmlFor="super-admin-email" className="block mb-2 font-semibold text-gray-800 text-sm">
              Admin Email Address*
            </label>
            <input
              type="email"
              id="super-admin-email"
              placeholder="admin@brillabee.com"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-slate-600 transition-colors"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="super-admin-password" className="block mb-2 font-semibold text-gray-800 text-sm">
              Password*
            </label>
            <input
              type="password"
              id="super-admin-password"
              placeholder="Enter password"
              className="w-full py-3 px-4 border-2 border-gray-200 rounded-xl bg-white focus:outline-none focus:border-slate-600 transition-colors"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
        </div>

        <button
          type="submit"
          className={`w-full bg-gradient-to-r from-slate-700 to-slate-800 text-white font-semibold py-4 px-6 rounded-2xl
                     hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide mb-4 ${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In as Admin'}
        </button>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
          </div>
        )}

        <div className="mt-4 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl text-xs text-yellow-800 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>This is a restricted area. Unauthorized access is prohibited.</span>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SuperAdminLoginPage;
